/**
 * AuditItemsFilters Component
 *
 * Provides comprehensive filtering and sorting functionality for audit items.
 * Features a compact main bar with expandable filter card for detailed filtering.
 * Based on the existing AuditSessionsFilters pattern but tailored for audit items.
 */

import { Card } from '@soteria/design-system-ve';
import { useState, useCallback, useMemo } from 'react';

import * as styles from './AuditItemsFilters.css';
import { FilterActions } from './FilterActions';
import { FilterBar } from './FilterBar';
import { FilterGrid } from './FilterGrid';
import { hasActiveAuditItemFilters, getActiveAuditItemFilterCount } from './helpers';
import type { AuditItemsFiltersProps, AuditItemFilters, AuditItemPagination } from './types';
import { createEmptyAuditItemFilters } from './types';

export function AuditItemsFilters({
  filters,
  onFiltersChange,
  pagination,
  onPaginationChange,
  filterOptions,
  filteredCount,
  totalCount,
  isLoading = false,
}: AuditItemsFiltersProps) {
  // UI state for filter expansion
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);

  // Check if any filters are active
  const filtersActive = useMemo(() => hasActiveAuditItemFilters(filters), [filters]);

  // Count active filters for display
  const activeFilterCount = useMemo(() => getActiveAuditItemFilterCount(filters), [filters]);

  // Handle filter toggle
  const handleFilterToggle = useCallback(() => {
    setIsFilterExpanded(prev => !prev);
  }, []);

  // Handle page size change
  const handlePageSizeChange = useCallback(
    (selectedItem: string) => {
      const newPagination: AuditItemPagination = {
        currentPage: 1, // Reset to first page when changing page size
        pageSize: Number(selectedItem) as AuditItemPagination['pageSize'],
      };

      onPaginationChange(newPagination);
    },
    [onPaginationChange]
  );

  // Individual filter handlers for the new single-value structure
  const handleStatusFilterChange = useCallback(
    (selectedValue: string | undefined) => {
      onFiltersChange({ ...filters, status: selectedValue as AuditItemFilters['status'] });
    },
    [filters, onFiltersChange]
  );

  const handleLabelFilterChange = useCallback(
    (selectedValue: string | undefined) => {
      onFiltersChange({ ...filters, label: selectedValue });
    },
    [filters, onFiltersChange]
  );

  const handleSeverityFilterChange = useCallback(
    (selectedValue: number | undefined) => {
      onFiltersChange({ ...filters, severity: selectedValue });
    },
    [filters, onFiltersChange]
  );

  const handleMultipleDecisionsStatusFilterChange = useCallback(
    (selectedValue: string | undefined) => {
      onFiltersChange({
        ...filters,
        multipleDecisionsStatus: selectedValue as AuditItemFilters['multipleDecisionsStatus'],
      });
    },
    [filters, onFiltersChange]
  );

  // Handle clear all filters
  const handleClearAllFilters = useCallback(() => {
    onFiltersChange(createEmptyAuditItemFilters());
  }, [onFiltersChange]);

  return (
    <div data-audit-filters-container className={styles.filtersContainer} data-loading={isLoading}>
      {/* Main Filter Bar */}
      <FilterBar
        activeFilterCount={activeFilterCount}
        filteredCount={filteredCount}
        filtersActive={filtersActive}
        isFilterExpanded={isFilterExpanded}
        isLoading={isLoading}
        pagination={pagination}
        totalCount={totalCount}
        onFilterToggle={handleFilterToggle}
        onPageSizeChange={handlePageSizeChange}
      />

      {/* Expandable Filter Card */}
      <div className={styles.filterCardContainer} data-expanded={isFilterExpanded}>
        <Card>
          <div className={styles.filterCard}>
            <FilterGrid
              filterOptions={filterOptions}
              filters={filters}
              isLoading={isLoading}
              onLabelFilterChange={handleLabelFilterChange}
              onMultipleDecisionsStatusFilterChange={handleMultipleDecisionsStatusFilterChange}
              onSeverityFilterChange={handleSeverityFilterChange}
              onStatusFilterChange={handleStatusFilterChange}
            />

            <FilterActions
              activeFilterCount={activeFilterCount}
              filtersActive={filtersActive}
              isLoading={isLoading}
              onClearAllFilters={handleClearAllFilters}
            />
          </div>
        </Card>
      </div>
    </div>
  );
}
